<script setup lang="ts">
import { ref, watch } from "vue";
import { isNullish, TuButton } from "@tinyurldev/tu-ui-vue";

import { logError } from "@Resources/js/helpers/logger";
import { useTranslation } from "@Resources/js/composables";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import type { BrandedDomainCheckResult } from "@Modules/teams/Api/DomainsApi";

//
// Setup
const authStore = XAuthStore();
const { __ } = useTranslation();

//
// Props
interface Props {
  domain?: string | null;
  nameservers: string[];
  checkDomain: (domain: string) => Promise<BrandedDomainCheckResult>;
}

const props = defineProps<Props>();

//
// Data
const busy = ref(false);
const domainIsActive = ref<boolean | null>(null);
const domainCheckMessage = ref<string | null>(null);

//
// Methods
async function check(): Promise<void> {
  if (isNullish(props.domain)) {
    logError("[CheckDomain]: Provided domain is nullish!");

    return;
  }

  busy.value = true;
  domainCheckMessage.value = null;

  try {
    const result = await props.checkDomain(props.domain);
    domainIsActive.value = result.active;
    domainCheckMessage.value = result.reason;
  } catch {
    domainCheckMessage.value = __("organization_domains_settings.check_added_domain.check_error");
  } finally {
    busy.value = false;
  }
}

//
// Watchers
watch(() => props.domain, () => {
  domainIsActive.value = null;
  domainCheckMessage.value = null;
});

watch(domainIsActive, (newValue, oldValue) => {
  if (oldValue !== null && oldValue !== newValue) {
    authStore.refreshApplicationState();
  }
});

//
// Exposed Test Vars
defineExpose({ busy, domainIsActive, domainCheckMessage, check });
</script>

<template>
  <div class="check-added-domain">
    <h4 class="check-added-domain__title">
      <template v-if="!isNullish(domainIsActive)">
        <i :class="`fa ${domainIsActive ? 'fa-check text-success' : 'fa-times text-danger'}`" />
      </template>

      {{ __("organization_domains_settings.check_added_domain.dns_setup_title") }}
    </h4>

    <p class="check-added-domain__description">
      {{ __("organization_domains_settings.check_added_domain.nameserver_description") }}
    </p>

    <h5 class="check-added-domain__nameserver-title">
      {{ __("organization_domains_settings.check_added_domain.nameserver_settings_title") }}
    </h5>

    <div class="check-added-domain__nameservers">
      <span
        v-for="nameserver in nameservers"
        :key="nameserver"
        class="check-added-domain__nameserver _nameserver"
      >
        {{ nameserver }}
      </span>
    </div>

    <p
      v-if="!domainIsActive"
      class="check-added-domain__propagation-note"
    >
      {{ __("organization_domains_settings.check_added_domain.propagation_note") }}
    </p>

    <div
      v-if="domainCheckMessage"
      class="check-added-domain__alert check-added-domain__alert--error"
    >
      {{ domainCheckMessage }}
    </div>

    <div
      v-else-if="domainIsActive"
      class="check-added-domain__alert check-added-domain__alert--success"
    >
      {{ __("organization_domains_settings.check_added_domain.success_message") }}
    </div>

    <TuButton
      class="_check"
      type="button"
      :label="__('organization_domains_settings.check_added_domain.check_button')"
      :is-loading="busy"
      :is-block="true"
      @click.stop="check"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.check-added-domain {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__title,
  &__nameserver-title,
  &__description,
  &__propagation-note {
    margin: 0;
  }

  &__nameservers {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxxs;
    padding: spacing.$xxs;
    border: border.$primary;
    border-radius: border.$medium-radius;
    background-color: color.$background-primary;
  }

  &__alert {
    padding: spacing.$xxs;
    border-radius: border.$medium-radius;

    &--error {
      background-color: color.$alert-light;
      color: color.$alert-dark;
    }

    &--success {
      background-color: color.$secondary-light;
      color: color.$secondary-dark;
    }
  }
}
</style>
